import { AnalyticsService } from 'base/analytics';
import { Button, ButtonPalette } from 'base/ui/button';
import { IconDesktop, IconMobile, IconTablet } from 'base/ui/icons';
import { DropdownIcon, MenuButton } from 'base/ui/menu';
import { menuDivider, menuItem } from 'base/ui/menu_model';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { shortcut, useKeyboardShortcuts } from '../keyboard_shortcuts';
import { CanvasViewport } from './canvas';
import { editorModeStore, isWireframeOrDesign } from './editor_mode';
import {
  Breakpoint,
  BREAKPOINTS,
  breakpointStore,
} from './wireframe/breakpoint';
import styles from './zoom_and_breakpoint_controls.css';

export function ZoomAndBreakpointControls({
  viewport,
  analytics,
}: {
  viewport: CanvasViewport;
  analytics: AnalyticsService;
}) {
  useZoomShortcuts(viewport);

  return (
    <ButtonPalette isDarker>
      {isWireframeOrDesign(editorModeStore.get()) && (
        <>
          {BREAKPOINTS.map((breakpoint) => (
            <BreakpointButton
              key={breakpoint}
              breakpoint={breakpoint}
              viewport={viewport}
              analytics={analytics}
            />
          ))}
          <div className={styles.divider} />
        </>
      )}
      <ZoomButton viewport={viewport} />
    </ButtonPalette>
  );
}

function useZoomShortcuts(viewport: CanvasViewport) {
  const zoomIn = viewport.makeZoomDeltaSnappedCallback(1);
  const zoomOut = viewport.makeZoomDeltaSnappedCallback(-1);
  const zoom100 = viewport.makeZoomSetCallback(0);

  useKeyboardShortcuts(true, {
    // Shift-+ is =
    [shortcut('+', { cmd: true, shift: true })]: zoomIn,
    [shortcut('=', { cmd: true })]: zoomIn,
    [shortcut('+', { shift: true })]: zoomIn,
    [shortcut('=')]: zoomIn,
    // Shift-- is _
    [shortcut('_', { cmd: true, shift: true })]: zoomOut,
    [shortcut('-', { cmd: true })]: zoomOut,
    [shortcut('_', { shift: true })]: zoomOut,
    [shortcut('-')]: zoomOut,
    // Shift-1 is !
    [shortcut('!', { shift: true })]: viewport.zoomToFit,
    [shortcut('0', { cmd: true })]: zoom100,
    // Shift-0 is )
    [shortcut(')', { shift: true })]: zoom100,
  });
}

export function zoomMenu(viewport: CanvasViewport) {
  const zoomIn = viewport.makeZoomDeltaSnappedCallback(1);
  const zoomOut = viewport.makeZoomDeltaSnappedCallback(-1);
  const zoom200 = viewport.makeZoomSetCallback(1);
  const zoom100 = viewport.makeZoomSetCallback(0);
  const zoom50 = viewport.makeZoomSetCallback(-1);
  return [
    menuItem({ label: 'Zoom in', onClick: zoomIn, shortcut: '⌘+' }),
    menuItem({ label: 'Zoom out', onClick: zoomOut, shortcut: '⌘-' }),
    menuItem({
      label: 'Zoom to Fit',
      onClick: viewport.zoomToFit,
      shortcut: '⇧1',
    }),
    menuDivider(),
    menuItem({
      label: '200%',
      onClick: zoom200,
      checked: Math.abs(viewport.zoom - 2) < 0.1,
    }),
    menuItem({
      label: '100%',
      onClick: zoom100,
      checked: Math.abs(viewport.zoom - 1) < 0.1,
      shortcut: '⌘0',
    }),
    menuItem({
      label: '50%',
      onClick: zoom50,
      checked: Math.abs(viewport.zoom - 0.5) < 0.05,
    }),
  ];
}

const ZoomButton = observer(function _ZoomButton({
  viewport,
}: {
  viewport: CanvasViewport;
}) {
  return (
    <MenuButton
      fullWidth={true}
      variant="subtle"
      label={Math.round(viewport.zoom * 100) + '%'}
      iconRight={DropdownIcon}
      menu={zoomMenu(viewport)}
      search="hidden"
    />
  );
});

const BreakpointButton = observer(function _BreakpointButton({
  breakpoint,
  viewport,
  analytics,
}: {
  breakpoint: Breakpoint;
  viewport: CanvasViewport;
  analytics: AnalyticsService;
}) {
  const isSelected = breakpointStore.get() === breakpoint;
  const onClick = () => {
    if (isSelected) return;
    viewport.maintainCursorPosition(() => {
      breakpointStore.set(breakpoint);
      analytics.track({ event: 'blocks/change-breakpoint', breakpoint });
    });
  };

  const key = BREAKPOINT_SHORTCUTS[breakpoint];
  useKeyboardShortcuts(true, {
    [shortcut(key)]: onClick,
  });

  return (
    <Button
      variant={(isSelected ? 'secondary' : 'subtle') as 'secondary'}
      onClick={onClick}
      tooltip={BREAKPOINT_TITLES[breakpoint]}
      tooltipShortcut={key}
      icon={BREAKPOINT_ICONS[breakpoint]}
    />
  );
});

const BREAKPOINT_SHORTCUTS: Record<Breakpoint, string> = {
  desktop: '1',
  tablet: '2',
  mobile: '3',
};
const BREAKPOINT_TITLES: Record<Breakpoint, string> = {
  desktop: 'Desktop',
  tablet: 'Tablet',
  mobile: 'Mobile',
};
const BREAKPOINT_ICONS: Record<Breakpoint, React.ComponentType> = {
  desktop: IconDesktop,
  tablet: IconTablet,
  mobile: IconMobile,
};
